import { motion } from "framer-motion";
import { Instagram, MapPin, MessageCircle } from "lucide-react";
import { BRAND, waLink } from "@/lib/site";
import { reveal } from "@/lib/motion";
import { WhatsAppButton } from "./WhatsAppButton";

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[#3B2818] text-[#FAF6F0] overflow-hidden"
    >
      {/* Big CTA */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-8 lg:px-12 pt-16 sm:pt-24 md:pt-36 pb-12 sm:pb-16">
        <motion.div {...reveal(0, { y: 24 })} className="max-w-5xl">
          <span className="text-[11px] uppercase tracking-[0.28em] text-[#C06A22]">
            Say hello
          </span>
          <h2 className="font-serif text-[36px] sm:text-6xl md:text-8xl lg:text-[128px] leading-[1.0] sm:leading-[0.9] tracking-tight mt-4 sm:mt-6">
            Hungry?
            <br />
            <span className="italic font-light text-[#C06A22]">
              Message us on
            </span>
            <br />
            WhatsApp.
          </h2>
          <p className="mt-4 sm:mt-8 max-w-lg text-sm sm:text-base leading-relaxed text-[#FAF6F0]/70">
            Tell us what you'd love, when you need it, and we'll start planning
            the batch. Usually a reply within the hour.
          </p>

          <div className="mt-8 sm:mt-10 flex flex-wrap items-center gap-4">
            <WhatsAppButton
              variant="whatsapp"
              size="lg"
              label={`WhatsApp`}
              testId="footer-wa-cta"
            />
            {BRAND.instagramUrl && (
              <a
                data-testid="footer-instagram"
                href={BRAND.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-xs sm:text-sm uppercase tracking-[0.22em] text-[#FAF6F0]/80 hover:text-[#C06A22] transition-colors duration-200"
              >
                <Instagram className="h-4 w-4" strokeWidth={1.5} />
                {BRAND.instagramHandle}
              </a>
            )}
          </div>
        </motion.div>
      </div>

      {/* Info grid */}
      <div className="border-t border-[#FAF6F0]/15">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12 py-12 md:py-16 grid grid-cols-12 gap-6 md:gap-10">
          <div className="col-span-12 md:col-span-5">
            <div className="flex items-baseline gap-2">
              <span className="font-serif text-3xl md:text-4xl leading-none">
                Signature
              </span>
              <span className="font-script text-3xl md:text-4xl text-[#C06A22] -mt-1 leading-none">
                Spoon
              </span>
            </div>
            <p className="mt-4 max-w-xs text-sm text-[#FAF6F0]/70 leading-relaxed">
              A small home bakery run by a mother — fresh cakes, cookies,
              muffins and gift tins, made to order.
            </p>
          </div>

          <div className="col-span-6 md:col-span-3">
            <div className="text-[11px] uppercase tracking-[0.24em] text-[#FAF6F0]/75 mb-4 font-medium">
              Service area
            </div>
            <div className="flex items-start gap-2 text-[15px] text-[#FAF6F0]/90">
              <MapPin className="h-4 w-4 mt-1 shrink-0 text-[#C06A22]" strokeWidth={1.5} />
              <span data-testid="footer-service-area">
                {BRAND.city}
                <br />
                <span className="text-[#FAF6F0]/75 text-[13px]">
                  (Delivery on request)
                </span>
              </span>
            </div>

            <div className="mt-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#FAF6F0]/10 border border-[#FAF6F0]/15 text-xs text-[#FAF6F0]/90">
              <span className="font-semibold tracking-wider text-[#C06A22]">FSSAI Lic.</span>
              <span className="font-mono text-[11px] tracking-wide">{BRAND.fssaiNumber}</span>
            </div>

            <div className="mt-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#FAF6F0]/10 border border-[#FAF6F0]/15 text-xs text-[#FAF6F0]/90">
              <span className="font-semibold tracking-wider text-[#C06A22]">Contact</span>
              <span className="font-mono text-[11px] tracking-wide">7276362030, 9665867393</span>
            </div>
          </div>

          

          <div className="col-span-6 md:col-span-4">
            <div className="text-[11px] uppercase tracking-[0.24em] text-[#FAF6F0]/75 mb-4 font-medium">
              Direct
            </div>
            <a
              data-testid="footer-wa-link"
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-[15px] text-[#FAF6F0]/90 hover:text-[#C06A22] transition-colors duration-200"
            >
              <MessageCircle className="h-4 w-4 text-[#25D366]" strokeWidth={1.5} />
              WhatsApp
            </a>
            {BRAND.instagramUrl && (
              <a
                href={BRAND.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 flex items-center gap-2 text-[15px] text-[#FAF6F0]/90 hover:text-[#C06A22] transition-colors duration-200"
              >
                <Instagram className="h-4 w-4 text-[#C06A22]" strokeWidth={1.5} />
                {BRAND.instagramHandle}
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-[#FAF6F0]/15">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-[11px] uppercase tracking-[0.24em] text-[#FAF6F0]/75">
          <span>© {new Date().getFullYear()} Signature Spoon — Baked with love.</span>
          <div className="flex items-center gap-4">
            <span>Designed with Passion by PixelForge❤️</span>
          </div>
        </div>
      </div>

      {/* Giant word backdrop */}
      <div
        aria-hidden
        className="pointer-events-none select-none absolute -bottom-10 md:-bottom-16 left-0 right-0 text-center font-serif italic text-[22vw] leading-[0.8] text-[#FAF6F0]/[0.04] whitespace-nowrap"
      >
        signature spoon
      </div>
    </footer>
  );
}
